import React, { FC } from 'react';
import { PrimaryButton } from 'src/components/widgets/buttons';
import { Checkbox, CheckboxContainer } from 'src/components/widgets/checkbox';
import { ValidationMessageStyled } from 'src/components/widgets/validation/validationMessage';
import { ZTooltip } from 'src/components/widgets/ztooltip';
import Stack from 'src/components/widgets/stack/stack';
import { T } from 'src/shared/helpers';
import { useValidationContext, ValidationStateProvider } from 'src/shared/validation';
import styled from 'styled-components';

/**
 * Checkbox on the left, Proceed on the right, on one row. An unticked checkbox is flagged by its red outline only:
 * the error text would break the single row.
 */
const Footer = styled(Stack)`
    align-items: center;
    justify-content: space-between;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid ${(props) => props.theme.app.borderColor};

    ${ValidationMessageStyled} {
        display: none;
    }

    /* Between the default (14px) and compact (12px) sizes; the default 20px line height keeps it centred on the box. */
    ${CheckboxContainer} label {
        font-size: 13px;
    }
`;

const FooterButtons = styled(Stack)`
    flex-shrink: 0;

    ${PrimaryButton} {
        min-width: 110px;
    }
`;

const TooltipTarget = styled.span`
    display: inline-block;
`;

interface SwitchAcknowledgeFooterProps {
    checked: boolean;
    onChange: (value: boolean) => void;
    onProceed: () => void;
    /** External block (e.g. plan/product limits) that keeps Proceed disabled regardless of the checkbox. */
    proceedDisabled?: boolean;
    proceedTooltip?: string;
}

const SwitchAcknowledgeFooterInner: FC<SwitchAcknowledgeFooterProps> = ({ checked, onChange, onProceed, proceedDisabled = false, proceedTooltip }) => {
    const validationContext = useValidationContext();

    // Proceed stays enabled: the checkbox is a required field, so a click without it goes through the shared validation
    // (red outline on the checkbox) instead of continuing.
    const handleProceed = () => {
        if (!validationContext.submit({ scroll: false })) return;
        onProceed();
    };

    return (
        <Footer $horizontal $gap='16px'>
            <Checkbox
                label={T._("I've read the information above and I'm ready to proceed.", 'Admin')}
                checked={checked}
                onChange={onChange}
                validationKey='switchAcknowledgedInfo'
                validationLabel={T._('Confirmation', 'Admin')}
                validate={(value) => !value}
                validationMessage={T._('Please confirm you have read the information above.', 'Admin')}
            />
            <FooterButtons $horizontal $gap='12px'>
                <ZTooltip position='top' title={proceedDisabled ? (proceedTooltip ?? '') : ''}>
                    <TooltipTarget>
                        <PrimaryButton onClick={handleProceed} disabled={proceedDisabled}>
                            {T._('Proceed', 'Admin')}
                        </PrimaryButton>
                    </TooltipTarget>
                </ZTooltip>
            </FooterButtons>
        </Footer>
    );
};

/** The acknowledgement checkbox + Proceed footer shared by every step of the switch wizard, so all steps look and behave the same. */
export const SwitchAcknowledgeFooter: FC<SwitchAcknowledgeFooterProps> = (props) => (
    <ValidationStateProvider>
        <SwitchAcknowledgeFooterInner {...props} />
    </ValidationStateProvider>
);

export default SwitchAcknowledgeFooter;
